import { useCallback, useState } from 'react';
import { StyleSheet, View, ScrollView } from 'react-native';
import { FormInput, Text, theme } from '@socrait/react';

import { VoiceNameItem } from '~/store/setupStore';

export interface VoiceNameEditorProps {
  voiceNameItems: VoiceNameItem[];
  onVoiceNameItemsChange: (items: VoiceNameItem[]) => void;
  onEditingStateChange?: (isEditing: boolean) => void;
  showInstructionsByDefault?: boolean;
  showLabel?: boolean;
  addNameInputValue: string;
  setAddNameInputValue: (value: string) => void;
}

export const VoiceNameEditor = ({
  voiceNameItems,
  onVoiceNameItemsChange,
  onEditingStateChange,
  showInstructionsByDefault = true,
  showLabel = true,
  addNameInputValue,
  setAddNameInputValue,
}: VoiceNameEditorProps) => {
  const [addNameError, setAddNameError] = useState<string | null>(null);

  // Validates a single voice name item and returns the updated item
  const validateVoiceNameItem = useCallback((item: VoiceNameItem, index: number, allItems: VoiceNameItem[]) => {
    const trimmedValue = item.draft.trim();

    // Validate that the trimmed name is non-empty
    if (!trimmedValue) {
      return {
        ...item,
        validationState: 'Name cannot be empty' as const,
      };
    }

    // Validate that the trimmed name is not a duplicate (case-insensitive, excluding current index)
    const hasDuplicate = allItems.some(
      (otherItem, idx) => idx !== index && otherItem.draft.toLocaleLowerCase() === trimmedValue.toLocaleLowerCase(),
    );

    if (hasDuplicate) {
      return {
        ...item,
        validationState: `"${trimmedValue}" already exists`,
      };
    }

    // Valid: commit the draft to name
    return {
      ...item,
      name: trimmedValue,
      draft: trimmedValue,
      validationState: 'valid' as const,
    };
  }, []);

  // Handler for revalidating all items in error state
  const validateAndCommitAllItems = useCallback(
    (allItems?: VoiceNameItem[]) => {
      const updatedItems = (allItems || voiceNameItems).map((item, index) => {
        // Skip items that are already committed
        if (item.draft === item.name) {
          return item;
        }

        return validateVoiceNameItem(item, index, allItems || voiceNameItems);
      });

      onVoiceNameItemsChange(updatedItems);
    },
    [voiceNameItems, onVoiceNameItemsChange, validateVoiceNameItem],
  );

  // Handler for adding a new voice name
  const handleAddVoiceName = useCallback(() => {
    const trimmedName = addNameInputValue.trim();

    if (!trimmedName) {
      setAddNameError('Name cannot be empty');
      return;
    }

    // Check for duplicates (case-insensitive)
    const hasDuplicate = voiceNameItems.some(
      (item) => item.name.toLocaleLowerCase() === trimmedName.toLocaleLowerCase(),
    );

    if (hasDuplicate) {
      setAddNameError(`"${trimmedName}" already exists`);
      return;
    }

    // Add new valid item to the front of the array
    const newItem = {
      name: trimmedName,
      draft: trimmedName,
      validationState: 'valid' as const,
    };
    onVoiceNameItemsChange([newItem, ...voiceNameItems]);
    setAddNameInputValue('');
    setAddNameError(null);
  }, [addNameInputValue, voiceNameItems, onVoiceNameItemsChange, setAddNameInputValue]);

  // Handler for adding voice name input change - clear error when user types
  const handleAddNameInputChange = useCallback(
    (value: string) => {
      setAddNameInputValue(value);
      if (addNameError) {
        setAddNameError(null);
      }
    },
    [addNameError, setAddNameInputValue],
  );

  // Handler for editing an existing voice name (updates draft only, no validation)
  const handleEditVoiceName = useCallback(
    (index: number, newValue: string) => {
      const updatedItems = [...voiceNameItems];
      if (updatedItems[index]) {
        updatedItems[index] = {
          ...updatedItems[index],
          draft: newValue,
        };
      }
      onVoiceNameItemsChange(updatedItems);
    },
    [voiceNameItems, onVoiceNameItemsChange],
  );

  // Handler for committing a draft voice name (on Enter or blur)
  const handleCommitDraft = useCallback(
    (index: number) => {
      const item = voiceNameItems[index];
      if (!item) return;

      const updatedItems = [...voiceNameItems];

      const validatedItem = validateVoiceNameItem(item, index, updatedItems);
      updatedItems[index] = validatedItem;
      onVoiceNameItemsChange(updatedItems);

      validateAndCommitAllItems(updatedItems); // validate and commit all items to ensure consistency
    },
    [voiceNameItems, onVoiceNameItemsChange, validateVoiceNameItem, validateAndCommitAllItems],
  );

  // Handler for deleting a voice name
  const handleDeleteVoiceName = useCallback(
    (index: number) => {
      onEditingStateChange?.(false);

      const updatedItems = voiceNameItems.filter((_, i) => i !== index);
      onVoiceNameItemsChange(updatedItems);

      validateAndCommitAllItems(updatedItems); // validate and commit all items to ensure consistency
    },
    [voiceNameItems, onVoiceNameItemsChange, validateAndCommitAllItems, onEditingStateChange],
  );

  return (
    <View style={styles.container}>
      {showInstructionsByDefault && (
        <Text style={styles.subtitle}>
          Voice names are the names you use in class to call on the students. They could be a nickname, a first name, a
          last name, or a hybrid like &quot;John A.&quot;
        </Text>
      )}
      <View style={styles.formContainer}>
        <FormInput
          autoCorrect={false}
          blurOnSubmit={false}
          errorMessage={addNameError || undefined}
          hasError={!!addNameError}
          label={showLabel ? 'Type a name and press Enter:' : undefined}
          onChangeText={handleAddNameInputChange}
          onSubmit={handleAddVoiceName}
          placeholderText="Enter a voice name"
          telemetryLabel="voice-name-add-input"
          value={addNameInputValue}
          variant="list"
        />
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.voiceNamesListContainer}
          showsVerticalScrollIndicator={true}>
          {voiceNameItems.map((item, index) => (
            <FormInput
              autoCorrect={false}
              canDelete={true}
              errorMessage={item.validationState !== 'valid' ? item.validationState : undefined}
              hasError={item.validationState !== 'valid'}
              inputStyle={styles.voiceNameItem}
              key={`voice-name-${index}`}
              onBlur={() => {
                onEditingStateChange?.(false);
                handleCommitDraft(index);
              }}
              onChangeText={(newValue) => handleEditVoiceName(index, newValue)}
              onDelete={() => handleDeleteVoiceName(index)}
              onFocus={() => onEditingStateChange?.(true)}
              onSubmit={() => {
                // On web, blur is handled automatically after submit
                // The onBlur handler will fire and commit the draft
              }}
              placeholderText="Voice Name"
              telemetryLabel={'voice-name-edit-input'}
              value={item.draft}
              variant="mobile"
            />
          ))}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: theme.spacing.medium,
    minHeight: 0,
  },
  formContainer: {
    gap: theme.spacing.medium,
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  scrollView: {
    flex: 1,
  },
  voiceNamesListContainer: {
    gap: theme.spacing.medium,
    paddingBottom: 30,
  },
  subtitle: {
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.small,
  },
  voiceNameItem: {
    borderColor: theme.colors.gray,
  },
});
